import React, { useCallback, useState } from 'react';

import { track } from 'utils/kitAnalytics';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import { resetWorkspace } from 'containers/KitWorkspaceStorage/api';
import ConfirmDialog from './ConfirmDialog';
import { useKitTranslate } from './useKitTranslate';

/**
 * Shared "Start over" for the kit flow: confirm, reset THIS workspace on the
 * backend (wipes assemblies + kit lines + accessories, keeps the empty kit
 * shells), then restart the wizard. The workspace key is kept so the kits are
 * reused for the next order. Returns a trigger, the confirm-dialog node to
 * render, and the in-flight flag.
 */
export const useKitStartOver = () => {
  const t = useKitTranslate();
  const { workspaceKey } = useKitWorkspace();
  const [confirming, setConfirming] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const requestStartOver = useCallback(() => setConfirming(true), []);

  const confirm = useCallback(async () => {
    setIsResetting(true);
    try {
      if (workspaceKey) await resetWorkspace(workspaceKey);
    } catch {
      // Reload anyway: a fresh schema GET reconciles state from the backend.
    }
    track('session_reset', { workspaceKey: workspaceKey ?? undefined, reason: 'start_over' });
    window.location.hash = 'new';
    window.location.reload();
  }, [workspaceKey]);

  const dialog = confirming ? (
    <ConfirmDialog
      title={t('texts.kits.startOverConfirm.title')}
      message={t('texts.kits.startOverConfirm.message')}
      note={t('texts.kits.startOverConfirm.note')}
      confirmLabel={t('texts.kits.startOverConfirm.confirmBtn')}
      danger
      warn
      busy={isResetting}
      onConfirm={confirm}
      onCancel={() => setConfirming(false)}
    />
  ) : null;

  return { requestStartOver, dialog, isResetting };
};

export default useKitStartOver;
